/**
 * The case's Monitor page (PRD-0075 B): three panes. *Inbox* — the alerts, shared by the case
 * (ADR-0096), filtered by state, severity and rule and triaged by collaborators. *Rules* — every
 * rule under its name, and the form that makes one (ADR-0100). *Sources* — each standing source
 * with its last intake. Entities are watched from the entity itself (*Sledovať* in the graph
 * panel and on a person's page).
 *
 * Presentational: `features/monitor/CaseMonitor` reads the panes and passes them in.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { InboxPane, type MonitorInboxPane } from '@/components/monitor/InboxPane'
import type { RuleRow } from '@/components/monitor/RulesList'
import { RulesPane, type RulesPaneProps } from '@/components/monitor/RulesPane'
import { SourcesPane, type SourcesPaneProps } from '@/components/monitor/SourcesPane'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'

export type { MonitorInboxPane } from '@/components/monitor/InboxPane'

export const MONITOR_TABS = ['inbox', 'rules', 'sources'] as const
export type MonitorTab = (typeof MONITOR_TABS)[number]

export interface MonitorWorkspaceProps {
  /** The pane shown; left out, the workspace keeps its own and opens on the inbox. */
  tab?: { value: MonitorTab; onChange: (tab: MonitorTab) => void }
  inbox: MonitorInboxPane
  rules: RulesPaneProps
  /** Left out, the workspace offers no Sources pane. */
  sources?: SourcesPaneProps
}

const PANE = 'flex min-h-0 flex-1 flex-col overflow-y-auto px-4 pb-6 md:px-6'

/** The rules as the inbox detail finds one by its id; null while they are read. */
function rulesOf(rules: RulesPaneProps): readonly RuleRow[] | null {
  return rules.view.status === 'ready' ? rules.view.rows : null
}

export function MonitorWorkspace({ tab, inbox, rules, sources }: MonitorWorkspaceProps) {
  const { t } = useTranslation()
  const tabs = MONITOR_TABS.filter(pane => pane !== 'sources' || sources)
  const [own, setOwn] = useState<MonitorTab>('inbox')
  // The rule an inbox alert pointed at, ringed in the Rules tab until the reader leaves it.
  const [highlight, setHighlight] = useState<string | null>(null)
  const [picked, setPicked] = useState<string | null>(null)
  const value = tab?.value ?? own
  const show = (next: MonitorTab) => (tab ? tab.onChange(next) : setOwn(next))
  const control = {
    value,
    onValueChange: (next: string) => {
      const picked = MONITOR_TABS.find(pane => pane === next)
      if (!picked) return
      setHighlight(null)
      show(picked)
    },
  }
  const openRule = (ruleId: string) => {
    setHighlight(ruleId)
    show('rules')
  }
  return (
    <Tabs {...control} className="flex min-h-0 flex-1 flex-col gap-4 bg-background">
      <TabsList variant="line" className="mx-4 md:mx-6">
        {tabs.map(pane => (
          <TabsTrigger key={pane} value={pane}>
            {t(`cases.monitor.tabs.${pane}`)}
          </TabsTrigger>
        ))}
      </TabsList>
      <TabsContent value="inbox" className={PANE}>
        <InboxPane
          inbox={inbox}
          detail={{ rules: rulesOf(rules), onOpenRule: openRule }}
          selection={{ key: picked, onSelect: setPicked }}
        />
      </TabsContent>
      <TabsContent value="rules" className={PANE}>
        <RulesPane {...rules} highlightRuleId={highlight} />
      </TabsContent>
      {sources ? (
        <TabsContent value="sources" className={PANE}>
          <SourcesPane {...sources} />
        </TabsContent>
      ) : null}
    </Tabs>
  )
}
